import { z } from "zod"
import {
  branchIdSchema,
  correlationIdSchema,
  entityIdSchema,
  legalEntityIdSchema,
} from "./identifiers.js"

const base = {
  requestVersion: z.literal(1),
  correlationId: correlationIdSchema,
  idempotencyKey: z.string().regex(/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/),
  orderId: entityIdSchema,
  revisionId: entityIdSchema,
  legalEntityId: legalEntityIdSchema,
  branchId: branchIdSchema,
}

export const purchaseOrderLineV1Schema = z
  .object({
    itemMasterId: entityIdSchema,
    locationId: entityIdSchema,
    quantityMilli: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
    unitCostMinor: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
  })
  .strict()

const commercial = {
  ...base,
  supplierMasterId: entityIdSchema,
  termsCode: z.string().trim().min(1).max(140).nullable(),
  scheduleDate: z.string().date(),
  lines: z.array(purchaseOrderLineV1Schema).min(1).max(100),
}

const validateLines = (
  value: { lines: z.infer<typeof purchaseOrderLineV1Schema>[] },
  context: z.RefinementCtx,
) => {
  const keys = value.lines.map(
    (line) => `${line.itemMasterId}:${line.locationId}`,
  )
  if (
    new Set(keys).size !== keys.length ||
    value.lines.some(
      (line) => (line.quantityMilli * line.unitCostMinor) % 1000 !== 0,
    )
  ) {
    context.addIssue({
      code: "custom",
      message: "Purchase Order lines are invalid.",
    })
  }
}

export const purchaseOrderCreateV1Schema = z
  .object({
    ...commercial,
    expectedVersion: z.literal(0),
    purchaseRequestId: entityIdSchema,
    expectedPurchaseRequestVersion: z.number().int().positive(),
  })
  .strict()
  .superRefine(validateLines)

export const purchaseOrderReviseV1Schema = z
  .object({
    ...commercial,
    expectedVersion: z.number().int().positive(),
    reasonCode: z.string().regex(/^[a-z][a-z0-9_]{1,63}$/),
  })
  .strict()
  .superRefine(validateLines)

export const purchaseOrderSubmitV1Schema = z
  .object({
    ...base,
    expectedVersion: z.number().int().positive(),
  })
  .strict()

export const purchaseOrderDecisionV1Schema = z
  .object({
    ...base,
    expectedVersion: z.number().int().positive(),
    decisionId: entityIdSchema,
    decision: z.enum(["approve", "reject"]),
    reasonCode: z.string().regex(/^[a-z][a-z0-9_]{1,63}$/),
  })
  .strict()

export const purchaseOrderCancelV1Schema = z
  .object({
    ...base,
    expectedVersion: z.number().int().positive(),
    reasonCode: z.string().regex(/^[a-z][a-z0-9_]{1,63}$/),
  })
  .strict()

export const purchaseOrderResultV1Schema = z
  .object({
    entityType: z.literal("purchase_order"),
    entityId: entityIdSchema,
    legalEntityId: legalEntityIdSchema,
    branchId: branchIdSchema,
    state: z.enum([
      "draft",
      "pending_approval",
      "approved",
      "rejected",
      "ordered",
      "canceled",
    ]),
    version: z.number().int().positive(),
    purchaseRequestId: entityIdSchema,
    purchaseRequestVersion: z.number().int().positive(),
    supplierMasterId: entityIdSchema,
    currency: z.string().regex(/^[A-Z]{3}$/),
    totalMinor: z.number().int().nonnegative(),
    termsCode: z.string().max(140).nullable(),
    scheduleDate: z.string().date(),
    policyId: entityIdSchema,
    policyVersion: z.number().int().positive(),
    erpnextPurchaseOrder: z.string().min(1).max(240).nullable(),
    supplierCommitmentCreated: z.boolean(),
  })
  .strict()
  .superRefine((value, context) => {
    const committed =
      value.state === "ordered" && value.erpnextPurchaseOrder !== null
    if (value.supplierCommitmentCreated !== committed) {
      context.addIssue({
        code: "custom",
        message: "Supplier commitment state is inconsistent.",
      })
    }
  })

export type PurchaseOrderCreateV1 = z.infer<typeof purchaseOrderCreateV1Schema>
export type PurchaseOrderReviseV1 = z.infer<typeof purchaseOrderReviseV1Schema>
